import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { InputFilterDirective } from '@ose/directives';
import { TranslateModule } from '@ngx-translate/core';

type DropdownChangeCallback = (nextValue: unknown) => void;
type DropdownTouchCallback = () => void;

@Component({
  selector: 'lib-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, InputFilterDirective, TranslateModule],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DropdownComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DropdownComponent,
    },
  ],
})
export class DropdownComponent
  implements OnChanges, ControlValueAccessor, Validator
{
  @Input() label = '';
  @Input() searchLabel = '';
  @Input() isOpened = false;
  @Input() items: unknown[] = [];
  @Input() searchable = false;
  @Input() showLabel = true;
  @Input() valueField?: string;
  @Input() selectedItem?: unknown;
  @Input() searchInputFilter?: string;
  @Input() hasReset = false;

  @Output() readonly itemChange = new EventEmitter<unknown>();
  @Output() readonly afterItemsLoad = new EventEmitter<void>();

  @Output() readonly searchValueChange = new EventEmitter<string>();

  @ContentChild('itemTemplate')
  itemTemplate!: TemplateRef<unknown>;

  @ContentChild('selectedItemTemplate')
  selectedItemTemplate!: TemplateRef<unknown>;

  @ContentChild('placeHolder')
  placeHolder?: TemplateRef<unknown>;

  @ViewChild('searchInput')
  searchInput?: ElementRef<HTMLInputElement>;

  isRequired = false;
  disabled = false;
  searchValue = '';

  protected myControl?: AbstractControl;
  private currentValue?: unknown;
  private onChange: DropdownChangeCallback = () => undefined;
  private onTouched: DropdownTouchCallback = () => undefined;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.syncSelectedItem();
      this.afterItemsLoad.emit();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Node)) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(target);

    if (!clickedInside && this.isOpened) {
      this.isOpened = false;
      this.onTouched();
    }
  }

  registerOnChange(fn: DropdownChangeCallback): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: DropdownTouchCallback): void {
    this.onTouched = fn;
  }

  writeValue(value: unknown): void {
    this.currentValue = value;
    this.syncSelectedItem();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.isOpened = false;
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.isRequired = control.hasValidator(Validators.required);
    this.myControl = control;

    return null;
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }

    this.isOpened = !this.isOpened;

    if (!this.isOpened) {
      this.onTouched();
      return;
    }

    if (this.searchable) {
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      });
    }
  }

  selectItem(item: unknown): void {
    if (this.disabled) {
      return;
    }

    this.selectedItem = item;
    this.currentValue = this.getItemValue(item);
    this.isOpened = false;

    this.onChange(this.currentValue);
    this.onTouched();
    this.itemChange.emit(item);

    this.cdr.markForCheck();
  }

  onSearchInput(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this.searchValueChange.emit(target.value);
  }

  private syncSelectedItem(): void {
    this.selectedItem = this.items.find((item) =>
      Object.is(this.getItemValue(item), this.currentValue)
    );

    this.cdr.markForCheck();
  }

  private getItemValue(item: unknown): unknown {
    if (!this.valueField) {
      return item;
    }

    if (typeof item !== 'object' || item === null) {
      return undefined;
    }

    const typedItem = item as Record<string, unknown>;

    return typedItem[this.valueField];
  }
}

export interface DropdownModel {
  text: string;
  value?: unknown;
}
